<template>
  <div class="admin-login relative flex min-h-dvh w-full items-center justify-center px-4 py-10 sm:px-6">
    <Head :title="__('Login to Pointra')" />

    <div class="relative z-10 w-full max-w-[24rem] sm:max-w-[28rem] lg:max-w-[32rem]">
      <div class="admin-login__panel rounded-3xl border px-7 py-9 sm:px-8 sm:py-10">
        <div class="flex flex-col items-center text-center">
          <AppLogo class="admin-login__logo h-14 w-auto max-w-[8.5rem]" />

          <p class="mt-5 text-[11px] font-medium tracking-[0.18em] text-zinc-500">
            {{ __('Admin') }}
          </p>

          <h1 class="mt-1.5 text-[1.5rem] font-semibold tracking-tight text-zinc-50">
            Pointra
          </h1>

          <p class="mt-2 max-w-[17rem] text-sm leading-relaxed text-zinc-400">
            {{ __('Sign in to continue to the control panel.') }}
          </p>
        </div>

        <form
          class="mt-8 space-y-4 text-start"
          @submit.prevent="attempt"
        >
          <div>
            <label
              class="mb-1.5 block text-[13px] font-medium text-zinc-300"
              for="email"
            >
              {{ __('Email') }}
            </label>
            <input
              id="email"
              v-model="form.email"
              class="admin-login__field w-full"
              :class="{ 'admin-login__field--error': form.errors.has('email') }"
              type="email"
              name="email"
              dir="ltr"
              placeholder="admin@example.com"
              autocomplete="username"
              autofocus
              required
            >

            <HelpText
              v-if="form.errors.has('email')"
              class="mt-2 text-red-400"
            >
              {{ form.errors.first('email') }}
            </HelpText>
          </div>

          <div>
            <label
              class="mb-1.5 block text-[13px] font-medium text-zinc-300"
              for="password"
            >
              {{ __('Password') }}
            </label>
            <div class="relative">
              <input
                id="password"
                v-model="form.password"
                class="admin-login__field w-full"
                :class="{ 'admin-login__field--error': form.errors.has('password') }"
                :type="showPassword ? 'text' : 'password'"
                name="password"
                dir="ltr"
                :placeholder="__('Enter your password')"
                autocomplete="current-password"
                required
              >
              <button
                type="button"
                class="admin-login__eye absolute top-1/2 -translate-y-1/2 p-1 text-zinc-500 transition hover:text-zinc-200"
                :aria-label="showPassword ? __('Hide password') : __('Show password')"
                @click="showPassword = !showPassword"
              >
                <HeroiconsSolidEye
                  v-if="!showPassword"
                  class="size-5"
                />
                <HeroiconsSolidEyeOff
                  v-else
                  class="size-5"
                />
              </button>
            </div>

            <HelpText
              v-if="form.errors.has('password')"
              class="mt-2 text-red-400"
            >
              {{ form.errors.first('password') }}
            </HelpText>
          </div>

          <div class="admin-login__meta flex items-center justify-between gap-3 pt-0.5">
            <Checkbox
              dusk="remember-button"
              :model-value="form.remember"
              :label="__('Remember me')"
              @change="() => (form.remember = !form.remember)"
            />

            <div
              v-if="supportsPasswordReset || forgotPasswordPath !== false"
              class="ms-auto shrink-0"
            >
              <Link
                v-if="forgotPasswordPath === false"
                :href="$url('/password/reset')"
                class="text-[13px] font-medium text-zinc-400 transition hover:text-zinc-200"
                v-text="__('Forgot your password?')"
              />
              <a
                v-else
                :href="forgotPasswordPath"
                class="text-[13px] font-medium text-zinc-400 transition hover:text-zinc-200"
                v-text="__('Forgot your password?')"
              />
            </div>
          </div>

          <button
            type="submit"
            class="admin-login__submit mt-2 inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl text-[15px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="form.processing"
          >
            <span
              v-if="form.processing"
              class="size-4 animate-spin rounded-full border-2 border-zinc-900/20 border-t-zinc-900"
              aria-hidden="true"
            />
            <span>{{ form.processing ? __('Signing in...') : __('Login') }}</span>
          </button>
        </form>
      </div>

      <p class="mt-5 text-center text-xs text-zinc-600">
        {{ __('Restricted access · authorized staff only') }}
      </p>
    </div>
  </div>
</template>

<script>
import Auth from '@/layouts/Auth'
import { Checkbox } from 'laravel-nova-ui'
import { Head, Link } from '@inertiajs/inertia-vue3'
import HeroiconsSolidEye from '@/components/Heroicons/solid/HeroiconsSolidEye'
import HeroiconsSolidEyeOff from '@/components/Heroicons/solid/HeroiconsSolidEyeOff'

export default {
  name: 'LoginPage',

  layout: Auth,

  components: {
    Checkbox,
    Head,
    Link,
    HeroiconsSolidEye,
    HeroiconsSolidEyeOff,
  },

  data: () => ({
    showPassword: false,
    form: Nova.form({
      email: '',
      password: '',
      remember: false,
    }),
  }),

  mounted() {
    if (this.$page.props.flash?.error) {
      Nova.error(this.$page.props.flash.error)
    }
    if (this.$page.props.flash?.success) {
      Nova.success(this.$page.props.flash.success)
    }
  },

  methods: {
    async attempt() {
      try {
        const { redirect } = await this.form.post(Nova.url('/login'))

        let path = { url: Nova.url('/'), remote: true }

        if (redirect !== undefined && redirect !== null) {
          path = { url: redirect, remote: true }
        }

        Nova.visit(path)
      } catch (error) {
        if (error.response?.status === 500) {
          Nova.error(this.__('There was a problem submitting the form.'))
        }
      }
    },
  },

  computed: {
    supportsPasswordReset() {
      return Nova.config('withPasswordReset')
    },

    forgotPasswordPath() {
      return Nova.config('forgotPasswordPath')
    },
  },
}
</script>

<style scoped>
.admin-login {
  background: #09090b;
  color: #fafafa;
}

.admin-login__panel {
  background: #18181b !important;
  border-color: #27272a !important;
  box-shadow: none !important;
}

.admin-login__logo :deep(.brand-logo__image) {
  filter: none !important;
}

.admin-login__field {
  min-height: 2.75rem;
  border-radius: 0.75rem;
  border: 1px solid #3f3f46;
  background: #09090b;
  color: #fafafa;
  padding: 0.625rem 0.875rem;
  outline: none;
  box-shadow: none !important;
  transition: border-color 0.15s ease;
}

.admin-login__field::placeholder {
  color: #71717a;
}

.admin-login__field:focus {
  border-color: #71717a;
}

.admin-login__field--error {
  border-color: #f87171;
}

.admin-login__eye {
  right: 0.75rem;
  left: auto;
}

#password.admin-login__field {
  padding-right: 2.75rem;
  padding-left: 0.875rem;
}

.admin-login__meta :deep(label),
.admin-login__meta :deep(span) {
  color: #a1a1aa !important;
}

.admin-login__submit {
  background: #fafafa;
  color: #09090b;
  box-shadow: none !important;
}

.admin-login__submit:hover:not(:disabled) {
  background: #e4e4e7;
}
</style>
